import { useState, useEffect } from 'react'
import { get, post, put, del } from '../utils/api'
import { IconPlus, IconEdit, IconTrash, IconCheck, IconX, IconClipboardList } from '@tabler/icons-react'

const SECTIONS = ['setup', 'food', 'beverage', 'av', 'notes']
const SECTION_LABELS = { setup: 'Room Setup', food: 'Food & Beverage', beverage: 'Beverages', av: 'AV & Equipment', notes: 'Special Notes' }
const SETUPS = ['banquet', 'theater', 'classroom', 'u-shape', 'reception', 'boardroom', 'hollow_square']
const STATUSES = { draft: 'bg-secondary-lt', final: 'bg-green-lt', revised: 'bg-yellow-lt', cancelled: 'bg-red-lt' }

export default function BEOPage() {
  const [beos, setBeos] = useState([])
  const [events, setEvents] = useState([])
  const [selected, setSelected] = useState(null)
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({})
  const [items, setItems] = useState([])
  const [newItem, setNewItem] = useState({ section: 'setup', description: '', quantity: 1, price: 0 })
  const [loading, setLoading] = useState(true)

  const load = () => Promise.all([get('/beos'), get('/events')]).then(([b, e]) => { setBeos(b); setEvents(e) }).finally(() => setLoading(false))
  useEffect(() => { load() }, [])

  const selectBeo = async (id) => {
    const data = await get(`/beos/${id}`)
    setSelected(data)
    setItems(data.items || [])
    setEditing(false)
  }

  const startNew = () => {
    setSelected(null)
    setForm({ title: '', event_id: '', event_date: '', start_time: '', end_time: '', location: '', setup_style: 'banquet', guest_count: '', contact_name: '', contact_phone: '', notes: '' })
    setItems([])
    setEditing(true)
  }

  const startEdit = () => {
    setForm({ ...selected })
    setEditing(true)
  }

  const save = async () => {
    try {
      if (selected?.id) {
        const r = await put(`/beos/${selected.id}`, form)
        setSelected(r)
      } else {
        const r = await post('/beos', form)
        setSelected(r)
        await selectBeo(r.id)
      }
      setEditing(false)
      load()
    } catch (err) { alert(err.message) }
  }

  const addItem = async () => {
    if (!newItem.description.trim() || !selected?.id) return
    try {
      const r = await post(`/beos/${selected.id}/items`, newItem)
      setItems([...items, r])
      setNewItem({ section: newItem.section, description: '', quantity: 1, price: 0 })
    } catch (err) { alert(err.message) }
  }

  const removeItem = async (itemId) => {
    await del(`/beos/items/${itemId}`)
    setItems(items.filter(i => i.id !== itemId))
  }

  const finalize = async () => {
    if (!confirm('Finalize this BEO? This will bump the revision number.')) return
    const r = await post(`/beos/${selected.id}/finalize`)
    setSelected({ ...selected, ...r })
    load()
  }

  const deleteBeo = async (id) => {
    if (!confirm('Delete this BEO?')) return
    await del(`/beos/${id}`)
    setSelected(null)
    load()
  }

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>

  return (
    <div className="row g-4">
      {/* Left: BEO List */}
      <div className="col-lg-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h2 className="mb-0">BEOs</h2>
          <button className="btn btn-primary btn-sm d-flex align-items-center gap-1" onClick={startNew}><IconPlus size={16} /> New BEO</button>
        </div>
        <div className="card">
          <div className="list-group list-group-flush">
            {beos.map(b => (
              <button key={b.id} className={`list-group-item list-group-item-action ${selected?.id === b.id ? 'active' : ''}`} onClick={() => selectBeo(b.id)}>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div className="fw-medium">{b.title}</div>
                    <div style={{ fontSize: '0.8rem' }} className="text-secondary">{b.event_name || 'No event'} {b.event_date && `- ${new Date(b.event_date).toLocaleDateString()}`}</div>
                  </div>
                  <span className={`badge ${STATUSES[b.status] || 'bg-secondary-lt'}`}>{b.status}</span>
                </div>
              </button>
            ))}
            {!beos.length && <div className="list-group-item text-center text-secondary py-4">No BEOs yet</div>}
          </div>
        </div>
      </div>

      {/* Right: Detail/Edit */}
      <div className="col-lg-8">
        {editing ? (
          <div className="card">
            <div className="card-header d-flex justify-content-between"><h3 className="card-title mb-0">{selected?.id ? 'Edit BEO' : 'New BEO'}</h3>
              <div className="d-flex gap-2"><button className="btn btn-primary btn-sm" onClick={save}>Save</button><button className="btn btn-ghost-secondary btn-sm" onClick={() => setEditing(false)}>Cancel</button></div>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-8"><label className="form-label">Title</label><input className="form-control" value={form.title||''} onChange={e => setForm({...form, title: e.target.value})} placeholder="e.g. Dinner Reception" /></div>
                <div className="col-4"><label className="form-label">Event</label><select className="form-select" value={form.event_id||''} onChange={e => setForm({...form, event_id: e.target.value})}><option value="">Select event...</option>{events.map(e => <option key={e.id} value={e.id}>{e.event_name}</option>)}</select></div>
                <div className="col-4"><label className="form-label">Date</label><input type="date" className="form-control" value={form.event_date?.split('T')[0]||''} onChange={e => setForm({...form, event_date: e.target.value})} /></div>
                <div className="col-4"><label className="form-label">Start</label><input type="time" className="form-control" value={form.start_time||''} onChange={e => setForm({...form, start_time: e.target.value})} /></div>
                <div className="col-4"><label className="form-label">End</label><input type="time" className="form-control" value={form.end_time||''} onChange={e => setForm({...form, end_time: e.target.value})} /></div>
                <div className="col-4"><label className="form-label">Location</label><input className="form-control" value={form.location||''} onChange={e => setForm({...form, location: e.target.value})} placeholder="Grand Ballroom" /></div>
                <div className="col-4"><label className="form-label">Setup</label><select className="form-select" value={form.setup_style||'banquet'} onChange={e => setForm({...form, setup_style: e.target.value})}>{SETUPS.map(s => <option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}</select></div>
                <div className="col-4"><label className="form-label">Guests</label><input type="number" className="form-control" value={form.guest_count||''} onChange={e => setForm({...form, guest_count: e.target.value})} /></div>
                <div className="col-6"><label className="form-label">Contact</label><input className="form-control" value={form.contact_name||''} onChange={e => setForm({...form, contact_name: e.target.value})} /></div>
                <div className="col-6"><label className="form-label">Phone</label><input className="form-control" value={form.contact_phone||''} onChange={e => setForm({...form, contact_phone: e.target.value})} /></div>
                <div className="col-12"><label className="form-label">Notes</label><textarea className="form-control" rows="2" value={form.notes||''} onChange={e => setForm({...form, notes: e.target.value})} /></div>
              </div>
            </div>
          </div>
        ) : selected ? (
          <div>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div>
                <h2 className="mb-0">{selected.title}</h2>
                <span className="text-secondary">{selected.event_name} - Rev. {selected.revision}</span>
              </div>
              <div className="d-flex gap-2">
                {selected.status !== 'final' && <button className="btn btn-success btn-sm" onClick={finalize}><IconCheck size={16} /> Finalize</button>}
                <button className="btn btn-ghost-primary btn-sm" onClick={startEdit}><IconEdit size={16} /> Edit</button>
                <button className="btn btn-ghost-danger btn-sm" onClick={() => deleteBeo(selected.id)}><IconTrash size={16} /></button>
              </div>
            </div>
            <div className="card mb-3">
              <div className="card-body">
                <div className="row g-2" style={{ fontSize: '0.9rem' }}>
                  <div className="col-4"><strong>Date:</strong> {selected.event_date ? new Date(selected.event_date).toLocaleDateString() : '--'}</div>
                  <div className="col-4"><strong>Time:</strong> {selected.start_time || '--'} - {selected.end_time || '--'}</div>
                  <div className="col-4"><strong>Location:</strong> {selected.location || '--'}</div>
                  <div className="col-4"><strong>Setup:</strong> {selected.setup_style}</div>
                  <div className="col-4"><strong>Guests:</strong> {selected.guest_count}</div>
                  <div className="col-4"><strong>Status:</strong> <span className={`badge ${STATUSES[selected.status]}`}>{selected.status}</span></div>
                  {selected.notes && <div className="col-12 mt-2"><strong>Notes:</strong> {selected.notes}</div>}
                </div>
              </div>
            </div>

            {/* BEO Items by Section */}
            {SECTIONS.map(sec => {
              const sectionItems = items.filter(i => i.section === sec)
              return (
                <div key={sec} className="card mb-3">
                  <div className="card-header"><h4 className="card-title mb-0">{SECTION_LABELS[sec]}</h4></div>
                  {sectionItems.length > 0 && (
                    <div className="table-responsive">
                      <table className="table table-vcenter card-table mb-0">
                        <tbody>
                          {sectionItems.map(item => (
                            <tr key={item.id}>
                              <td>{item.description}</td>
                              <td style={{ width: 80 }} className="text-end">{item.quantity > 1 ? `x${item.quantity}` : ''}</td>
                              <td style={{ width: 90 }} className="text-end">{item.price > 0 ? `$${Number(item.price).toFixed(2)}` : ''}</td>
                              <td style={{ width: 40 }}><button className="btn btn-ghost-danger btn-sm btn-icon" onClick={() => removeItem(item.id)}><IconX size={14} /></button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {/* Quick add row */}
                  <div className="card-footer py-2">
                    <div className="d-flex gap-2 align-items-end">
                      <input className="form-control form-control-sm" placeholder={`Add ${SECTION_LABELS[sec].toLowerCase()} item...`}
                        value={newItem.section === sec ? newItem.description : ''} onChange={e => setNewItem({ ...newItem, section: sec, description: e.target.value })}
                        onKeyDown={e => { if (e.key === 'Enter' && newItem.section === sec) addItem() }} />
                      <input type="number" className="form-control form-control-sm" style={{ width: 70 }} placeholder="Qty"
                        value={newItem.section === sec ? newItem.quantity : 1} onChange={e => setNewItem({ ...newItem, section: sec, quantity: parseInt(e.target.value) || 1 })} />
                      <input type="number" className="form-control form-control-sm" style={{ width: 90 }} placeholder="Price" step="0.01"
                        value={newItem.section === sec && newItem.price ? newItem.price : ''} onChange={e => setNewItem({ ...newItem, section: sec, price: parseFloat(e.target.value) || 0 })} />
                      <button className="btn btn-sm btn-primary" onClick={() => { if (newItem.section !== sec) setNewItem({...newItem, section: sec}); else addItem(); }}>
                        <IconPlus size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="card"><div className="card-body text-center text-secondary py-5">
            <IconClipboardList size={48} className="mb-3 text-secondary" style={{ opacity: 0.3 }} />
            <p>Select a BEO or create a new one</p>
          </div></div>
        )}
      </div>
    </div>
  )
}
